import { Button, Dialog, Icon, Portal, Stack } from '@chakra-ui/react';
import React, { FunctionComponent as FC } from 'react';
import { MdClose } from 'react-icons/md';
import { useConfirmModal } from '../../services/confirmModal';
import { MText } from '../Monetize';
import { keepOpenForToasts } from '../Monetize/Overlays';

interface MConfirmModalProps {}

const MConfirmModal: FC<MConfirmModalProps> = () => {
  const initialRef = React.useRef(null);
  // Labels the dialog by its header, as v2's ModalHeader did — see
  // `MCenterModal.tsx`.
  const titleId = React.useId();
  const { modalData, closeModal, loading } = useConfirmModal();

  if (!modalData) {
    return null;
  }

  const onClose = () => {
    closeModal();
  };

  return (
    <Dialog.Root
      ids={{ title: titleId }}
      initialFocusEl={() => initialRef.current}
      preventScroll
      closeOnInteractOutside
      onInteractOutside={keepOpenForToasts}
      placement="center"
      motionPreset="slide-in-bottom"
      size="lg"
      {...modalData.modalProps}
      open
      scrollBehavior="inside"
      finalFocusEl={() => (modalData.finalFocusRef ?? initialRef).current}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-testid="confirm-modal">
            <Dialog.Header id={titleId} fontSize="lg" fontWeight="600" pr="10">
              {modalData.title}
            </Dialog.Header>
            {modalData.showCloseButton && (
              // v3's trigger is unnamed (icon is aria-hidden); v2's was "Close"
              <Dialog.CloseTrigger aria-label="Close">
                <Icon size="md" asChild>
                  <MdClose />
                </Icon>
              </Dialog.CloseTrigger>
            )}
            <Dialog.Body pt="0" pb="4" {...modalData.modalBodyProps}>
              {modalData.description && (
                <MText color="tGray.darkPurple" textAlign="left">
                  {modalData.description}
                </MText>
              )}
              {modalData.content}
            </Dialog.Body>

            <Dialog.Footer px="0" py="4" mx="4" {...modalData.modalFooterProps}>
              <Stack
                gap={4}
                direction="row"
                align="center"
                justify="right"
                flex={1}
              >
                {!!modalData.noButton && (
                  <Button
                    onClick={modalData.onNo}
                    variant="cancel"
                    minW="auto"
                    disabled={loading}
                    {...modalData.noBtnProps}
                  >
                    {modalData.noButton}
                  </Button>
                )}
                {!!modalData.yesButton && (
                  <Button
                    ref={initialRef}
                    variant="primary"
                    minW="auto"
                    {...modalData.yesBtnProps}
                    onClick={modalData.onYes}
                    loading={loading}
                    data-testid="modal-confirm-btn"
                  >
                    {modalData.yesButton}
                  </Button>
                )}
              </Stack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default MConfirmModal;
